(() => {
  "use strict";
  const dialog = document.querySelector("[data-outhavn-cart-drawer]");
  if (
    !dialog ||
    dialog.dataset.enabled === "false" ||
    window.OuthavnCart ||
    typeof dialog.showModal !== "function"
  )
    return;
  const $ = (selector) => dialog.querySelector(selector);
  const itemsNode = $("[data-cart-items]");
  const checkout = $("[data-cart-checkout]");
  if (!itemsNode || !checkout || !$("[data-cart-close]")) return;
  const settings = dialog.dataset;
  const pathRoot = /^\/(?:[A-Za-z0-9-]+\/)*$/.test(settings.root || "")
    ? settings.root
    : "/";
  const baseFetch = window.fetch;
  const productCache = new Map();
  const catalog = new Map();
  const selections = new Map();
  let cart = null;
  let fresh = false;
  let pending = 0;
  let externalPending = 0;
  let checkoutBusy =
    document.querySelector("[data-outhavn-cart-handoff]")?.dataset
      .checkoutBusy === "true";
  let revision = 0;
  let queue = Promise.resolve();
  let cartRequests = Promise.resolve();
  let refreshTimer;
  let recommendationVersion = 0;
  let recommendationKey = "";
  let restoreFocus = null;
  let priorOverflow = "";
  let announcement = "";
  const externalWaiters = [];

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = String(text);
    return node;
  };
  const minor = (value) => Number.isSafeInteger(value) && value >= 0;
  const money = (value) => {
    const currency = cart?.currency || settings.currency || "USD";
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency,
      }).format(value / 100);
    } catch {
      return `${(value / 100).toFixed(2)} ${currency}`;
    }
  };
  const handleOf = (product) => {
    const handle =
      product?.handle ||
      String(product?.url || "").match(/\/products\/([a-z0-9-]+)/i)?.[1];
    return typeof handle === "string" &&
      /^[a-z0-9][a-z0-9-]{0,199}$/i.test(handle)
      ? handle
      : null;
  };
  const productUrl = (product) =>
    `${pathRoot}products/${handleOf(product) || ""}`;
  const image = (source, title, className) => {
    const node = element("img", className);
    node.alt = title || "";
    node.loading = "lazy";
    try {
      const url = new URL(
        typeof source === "string" ? source : source?.url || source?.src,
        location.origin,
      );
      if (
        url.protocol === "https:" ||
        (url.origin === location.origin && url.protocol === "http:")
      )
        node.src = url.href;
    } catch {
      /* An absent image never becomes markup or an unsafe URL. */
    }
    return node;
  };
  const button = (text, label, className, action) => {
    const node = element("button", className, text);
    node.type = "button";
    node.setAttribute("aria-label", label);
    if (action) node.dataset.cartAction = action;
    return node;
  };
  const trashIcon = () => {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("aria-hidden", "true");
    const path = document.createElementNS(svg.namespaceURI, "path");
    path.setAttribute(
      "d",
      "M4 7h16M9 7V4h6v3M6 7l1 14h10l1-14M10 11v6M14 11v6",
    );
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "1.5");
    svg.append(path);
    return svg;
  };
  function showError(message, retry = false) {
    const node = $("[data-cart-error]");
    node.textContent = String(
      message || "Your cart could not be updated. Please try again.",
    ).slice(0, 300);
    node.hidden = false;
    $("[data-cart-retry]").hidden = !retry;
  }
  function clearError() {
    $("[data-cart-error]").hidden = true;
    $("[data-cart-error]").textContent = "";
    $("[data-cart-retry]").hidden = true;
  }
  function state() {
    const busy = pending > 0 || externalPending > 0 || checkoutBusy;
    checkout.disabled = busy || !fresh || !cart?.items?.length;
    dialog.setAttribute("aria-busy", String(busy));
    $("[data-cart-status]").textContent = checkoutBusy
      ? "Opening checkout…"
      : busy
        ? cart
          ? "Updating cart…"
          : "Loading cart…"
        : announcement;
    for (const node of dialog.querySelectorAll("[data-cart-action]"))
      node.disabled = !fresh || externalPending > 0 || checkoutBusy;
    for (const node of dialog.querySelectorAll("[data-upsell-add]"))
      node.disabled = busy || !fresh;
    for (const node of dialog.querySelectorAll(".outhavn-cart-upsell select"))
      node.disabled = busy || !fresh;
    document.dispatchEvent(
      new CustomEvent("outhavn:cart:state", {
        detail: { open: dialog.open, pending: busy },
      }),
    );
  }
  function validCart(value) {
    if (
      !value ||
      !Array.isArray(value.items) ||
      !minor(value.total_price) ||
      !minor(value.item_count) ||
      !/^[A-Z]{3}$/.test(value.currency)
    )
      throw new Error("Your cart could not be read. Please try again.");
    return value;
  }
  async function request(resource, options = {}) {
    const response = await cartFetch(window, [
      new URL(pathRoot + resource, location.origin).href,
      {
        credentials: "same-origin",
        cache: "no-store",
        ...options,
        headers: {
          Accept: "application/json",
          ...(options.body ? { "Content-Type": "application/json" } : {}),
          ...options.headers,
        },
        signal: AbortSignal.timeout(15000),
      },
    ]);
    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error("Your cart could not be read. Please try again.");
    }
    if (!response.ok)
      throw new Error(
        typeof data.description === "string"
          ? data.description
          : "This item could not be updated. Please check its availability and try again.",
      );
    return data;
  }
  function lineIdentity(line) {
    const properties = Object.entries(line.properties || {}).sort(([a], [b]) =>
      a.localeCompare(b),
    );
    return JSON.stringify([
      String(line.variant_id || line.id),
      properties,
      line.selling_plan_allocation?.selling_plan?.id || null,
    ]);
  }
  function setCart(value) {
    cart = validCart(value);
    fresh = true;
    render();
    document.dispatchEvent(
      new CustomEvent("outhavn:cart:updated", { detail: { cart } }),
    );
    void recommendations();
  }
  async function readCart() {
    await waitForExternal();
    const started = revision;
    const result = await request("cart.js");
    if (started !== revision || externalPending) {
      fresh = false;
      scheduleRefresh(false);
      return;
    }
    setCart(result);
  }
  function waitForExternal() {
    return externalPending
      ? new Promise((resolve) => externalWaiters.push(resolve))
      : Promise.resolve();
  }
  function enqueue(work) {
    pending++;
    announcement = "";
    clearError();
    state();
    const run = queue.then(async () => {
      await waitForExternal();
      try {
        await work();
      } catch (error) {
        fresh = false;
        // A failed response can follow a successful write. Re-read before checkout.
        try {
          await readCart();
        } catch {
          /* Retry stays visible if state is unknown. */
        }
        showError(error?.message, !fresh);
      } finally {
        pending--;
        state();
      }
    });
    queue = run.catch(() => {});
    return run;
  }
  function refresh() {
    return enqueue(readCart);
  }
  function open(trigger) {
    if (!dialog.open) {
      restoreFocus =
        trigger instanceof HTMLElement ? trigger : document.activeElement;
      priorOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      dialog.showModal();
      $("[data-cart-close]").focus({ preventScroll: true });
    }
    state();
    return refresh();
  }
  function close() {
    if (!dialog.open) return;
    dialog.close();
    document.body.style.overflow = priorOverflow;
    if (restoreFocus?.isConnected && typeof restoreFocus.focus === "function")
      restoreFocus.focus({ preventScroll: true });
    state();
  }
  function change(line, adjustment) {
    if (checkoutBusy) return Promise.resolve();
    const identity = lineIdentity(line);
    return enqueue(async () => {
      if (checkoutBusy) return;
      if (!fresh) await readCart();
      if (!fresh)
        throw new Error("Your cart is still updating. Please try again.");
      const matches = cart.items.filter(
        (item) => item.key === line.key || lineIdentity(item) === identity,
      );
      if (matches.length !== 1)
        throw new Error(
          "This cart item changed. Please review your cart and try again.",
        );
      const current = matches[0];
      const quantity =
        adjustment === "remove"
          ? 0
          : Math.max(0, current.quantity + adjustment);
      if (!Number.isSafeInteger(quantity) || quantity > 999)
        throw new Error("This quantity is not available.");
      const started = ++revision;
      const response = await request("cart/change.js", {
        method: "POST",
        body: JSON.stringify({ id: current.key, quantity }),
      });
      // Sending the line key without a properties field preserves every original property.
      if (started === revision && !externalPending) setCart(response);
      else await readCart();
      announcement = quantity ? "Cart updated." : "Item removed from cart.";
    });
  }
  function render() {
    const focused = document.activeElement?.closest?.("[data-cart-action]");
    const focusKey = focused?.closest("[data-line-key]")?.dataset.lineKey;
    const focusIdentity =
      focused?.closest("[data-line-key]")?.dataset.lineIdentity;
    const focusAction = focused?.dataset.cartAction;
    const fragment = document.createDocumentFragment();
    if (!cart.items.length) {
      const empty = element(
        "div",
        "outhavn-cart-empty",
        settings.emptyLabel || "Your cart is empty.",
      );
      const keepShopping = button(
        "Continue shopping",
        "Continue shopping",
        "",
        "continue",
      );
      keepShopping.addEventListener("click", close);
      empty.append(keepShopping);
      fragment.append(empty);
    }
    for (const line of cart.items) fragment.append(renderLine(line));
    itemsNode.replaceChildren(fragment);
    $("[data-cart-count]").textContent = cart.item_count;
    for (const region of document.querySelectorAll(
      'cart-live-region[id^="cartCounter-"]',
    )) {
      let counter = region.querySelector(
        '[class~="#header-toolbar-cart-counter"]',
      );
      if (!counter && cart.item_count) {
        counter = element("span", "#header-toolbar-cart-counter");
        (
          region.querySelector('[class~="#header-toolbar-item-icon"]') || region
        ).append(counter);
      }
      if (counter) {
        counter.textContent = cart.item_count;
        counter.hidden = cart.item_count === 0;
      }
    }
    $("[data-cart-total]").textContent = money(cart.total_price);
    const discounts = $("[data-cart-discounts]");
    discounts.replaceChildren();
    for (const discount of cart.cart_level_discount_applications || []) {
      if (
        !minor(discount.total_allocated_amount) ||
        !discount.total_allocated_amount
      )
        continue;
      const row = element("p");
      row.append(
        element("span", "", discount.title || "Discount"),
        element("span", "", `−${money(discount.total_allocated_amount)}`),
      );
      discounts.append(row);
    }
    renderProgress();
    state();
    if (focusAction) {
      const row = [...itemsNode.querySelectorAll("[data-line-key]")].find(
        (node) =>
          node.dataset.lineKey === focusKey ||
          node.dataset.lineIdentity === focusIdentity,
      );
      const target = [
        ...(row?.querySelectorAll("[data-cart-action]") || []),
      ].find((node) => node.dataset.cartAction === focusAction);
      (target || $("[data-cart-close]")).focus({ preventScroll: true });
    }
  }
  function renderLine(line) {
    const title = line.product_title || line.title || "Cart item";
    const row = element("article", "outhavn-cart-item");
    row.dataset.lineKey = line.key;
    row.dataset.lineIdentity = lineIdentity(line);
    row.append(
      image(
        line.featured_image?.url || line.image,
        title,
        "outhavn-cart-item-image",
      ),
    );
    const body = element("div");
    const heading = element("h3");
    if (handleOf(line)) {
      const link = element("a", "", title);
      link.href = productUrl(line);
      heading.append(link);
    } else heading.textContent = title;
    body.append(heading);
    if (line.variant_title && line.variant_title !== "Default Title")
      body.append(element("p", "outhavn-cart-variant", line.variant_title));
    for (const [key, value] of Object.entries(line.properties || {})) {
      if (!key.startsWith("_") && value !== null && value !== "")
        body.append(element("p", "outhavn-cart-property", `${key}: ${value}`));
    }
    const final = minor(line.final_line_price)
      ? line.final_line_price
      : minor(line.line_price)
        ? line.line_price
        : 0;
    const variant = catalog
      .get(handleOf(line))
      ?.variants?.find(
        (item) => String(item.id) === String(line.variant_id || line.id),
      );
    const catalogCompare = minor(variant?.compare_at_price)
      ? variant.compare_at_price * line.quantity
      : 0;
    const original = Math.max(
      minor(line.original_line_price) ? line.original_line_price : final,
      catalogCompare,
    );
    const prices = element("div", "outhavn-cart-prices");
    if (original > final) prices.append(element("s", "", money(original)));
    prices.append(element("strong", "", money(final)));
    body.append(prices);
    if (original > final)
      body.append(
        element("span", "outhavn-cart-save", `Save ${money(original - final)}`),
      );
    const actions = element("div", "outhavn-cart-item-actions");
    const quantity = element("div", "outhavn-cart-quantity");
    const minus = button("−", `Decrease quantity of ${title}`, "", "minus");
    const plus = button("+", `Increase quantity of ${title}`, "", "plus");
    const number = element("output", "", line.quantity);
    number.setAttribute("aria-label", `Quantity of ${title}`);
    minus.addEventListener("click", () => void change(line, -1));
    plus.addEventListener("click", () => void change(line, 1));
    quantity.append(minus, number, plus);
    const remove = button(
      "",
      `Remove ${title}`,
      "outhavn-cart-remove",
      "remove",
    );
    remove.append(trashIcon());
    remove.addEventListener("click", () => void change(line, "remove"));
    actions.append(quantity, remove);
    body.append(actions);
    row.append(body);
    return row;
  }
  function physical(product) {
    const text = `${product.product_type || product.type || ""} ${product.product_title || product.title || ""}`;
    return (
      product.gift_card !== true &&
      product.requires_shipping !== false &&
      !/gift\s*card|shipping\s*protection|shipping\s*insurance|warranty|\bservice\b/i.test(
        text,
      )
    );
  }
  function renderProgress() {
    const node = $("[data-cart-progress]");
    node.hidden = true;
    node.replaceChildren();
    if (settings.promotionsEnabled !== "true" || !cart.items.length) return;
    const tiers = [1, 2, 3].map((index) => ({
      quantity: Number(dialog.getAttribute(`data-tier-${index}`)),
      label: dialog.getAttribute(`data-tier-${index}-label`) || "",
    }));
    if (
      !tiers.every(
        (tier, index) =>
          Number.isInteger(tier.quantity) &&
          tier.quantity > (tiers[index - 1]?.quantity || 0) &&
          tier.label.trim(),
      )
    )
      return;
    let eligibleIds = null;
    if (settings.eligibleProductIds?.trim()) {
      try {
        const ids = JSON.parse(settings.eligibleProductIds);
        if (
          !Array.isArray(ids) ||
          !ids.every(
            (id) =>
              typeof id === "string" &&
              /^(?:gid:\/\/shopify\/Product\/)?[1-9]\d*$/.test(id),
          )
        )
          return;
        eligibleIds = new Set(
          ids.map((id) => id.replace("gid://shopify/Product/", "")),
        );
      } catch {
        return;
      }
    }
    const count = cart.items
      .filter(
        (line) =>
          physical(line) &&
          (!eligibleIds || eligibleIds.has(String(line.product_id))),
      )
      .reduce((sum, line) => sum + line.quantity, 0);
    const next = tiers.find((tier) => count < tier.quantity);
    const remaining = next ? next.quantity - count : 0;
    const headline = element(
      "p",
      "outhavn-cart-progress-headline",
      next
        ? `Add ${remaining} ${remaining === 1 ? "product" : "products"} and get ${next.label}`
        : `${tiers[2].label} unlocked`,
    );
    const track = element("div", "outhavn-cart-progress-track");
    const fill = element("span", "outhavn-cart-progress-fill");
    const reached = tiers.filter((tier) => count >= tier.quantity).length;
    const fraction =
      reached === 0
        ? 0
        : reached >= 3
          ? 1
          : (reached -
              1 +
              (count - tiers[reached - 1].quantity) /
                (tiers[reached].quantity - tiers[reached - 1].quantity)) /
            2;
    fill.style.width = `${Math.max(0, Math.min(100, fraction * 100))}%`;
    track.setAttribute("role", "progressbar");
    track.setAttribute("aria-label", "Promotion item milestones");
    track.setAttribute("aria-valuemin", "0");
    track.setAttribute("aria-valuemax", String(tiers[2].quantity));
    track.setAttribute(
      "aria-valuenow",
      String(Math.min(count, tiers[2].quantity)),
    );
    track.append(fill);
    const milestones = element("div", "outhavn-cart-progress-milestones");
    for (const tier of tiers) {
      const milestone = element("div", "outhavn-cart-progress-milestone");
      milestone.dataset.reached = String(count >= tier.quantity);
      milestone.setAttribute(
        "aria-label",
        `${tier.quantity} eligible items: ${tier.label}`,
      );
      const marker = element("span", "outhavn-cart-progress-marker");
      const icon = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "svg",
      );
      icon.setAttribute("viewBox", "0 0 24 24");
      icon.setAttribute("aria-hidden", "true");
      const path = document.createElementNS(icon.namespaceURI, "path");
      path.setAttribute("d", "M3 4h8l10 10-7 7L3 10V4Z M7 7h.01");
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "currentColor");
      path.setAttribute("stroke-width", "1.6");
      path.setAttribute("stroke-linecap", "round");
      path.setAttribute("stroke-linejoin", "round");
      icon.append(path);
      marker.append(icon);
      const label = element(
        "span",
        "outhavn-cart-progress-label",
        `EXTRA ${tier.label.replace(/\s+off$/i, "")}`,
      );
      milestone.append(marker, label);
      milestones.append(milestone);
    }
    const visual = element("div", "outhavn-cart-progress-visual");
    visual.append(track, milestones);
    node.append(headline, visual);
    node.hidden = false;
  }
  async function hydrate(candidate) {
    const handle = handleOf(candidate);
    if (!handle) return null;
    if (!productCache.has(handle))
      productCache.set(
        handle,
        request(`products/${encodeURIComponent(handle)}.js`)
          .then((product) => {
            if (!product || !Array.isArray(product.variants))
              throw new Error("Product details unavailable.");
            catalog.set(handle, product);
            return product;
          })
          .catch(() => {
            productCache.delete(handle);
            return null;
          }),
      );
    return productCache.get(handle);
  }
  function classification(product) {
    const text =
      `${product.title || product.product_title || ""} ${product.product_type || product.type || ""} ${Array.isArray(product.tags) ? product.tags.join(" ") : product.tags || ""}`.toLowerCase();
    const categories = [
      [
        "halloween",
        /halloween|pumpkin|spooky|witch|skeleton|werewolf|costume|ghost|haunted/,
      ],
      ["christmas", /christmas|santa|reindeer|snowman|xmas|nativity/],
      [
        "travel",
        /travel|luggage|backpack|packing|suitcase|passport|toiletry|duffel|weekender/,
      ],
      ["pet", /\bpet\b|\bdog\b|\bcat\b|leash|litter/],
      [
        "kitchen",
        /kitchen|cooking|cookware|utensil|cutting board|food storage/,
      ],
      ["garden", /garden|planter|gardening|bird feeder/],
      ["home decor", /home decor|wall decor|vase|candle|wreath/],
    ];
    const known = categories.find(([, pattern]) => pattern.test(text));
    if (known) return known[0];
    const type = String(product.product_type || product.type || "")
      .trim()
      .toLowerCase();
    return type &&
      !/^(?:accessories|other|general|products|sale|default)$/.test(type)
      ? type.slice(0, 80)
      : null;
  }
  const usableVariants = (product) =>
    (product.variants || []).filter(
      (variant) =>
        variant.available === true &&
        variant.requires_shipping !== false &&
        minor(variant.price),
    );
  async function recommendations() {
    const limit = Math.min(6, Math.max(0, Number(settings.upsellCount) || 0));
    const products = cart.items.filter(physical);
    const key = products
      .map((line) => `${line.product_id}:${handleOf(line)}`)
      .sort()
      .join("|");
    if (key === recommendationKey) return;
    recommendationKey = key;
    const ownVersion = ++recommendationVersion;
    const area = $("[data-cart-upsells]");
    area.hidden = true;
    $("[data-cart-upsell-list]").replaceChildren();
    if (!products.length) return;
    const seed = await hydrate(products[0]);
    if (ownVersion !== recommendationVersion) return;
    // Catalog compare-at prices are optional; cart prices remain authoritative.
    if (seed && cart) render();
    if (!limit || !seed || !physical(seed)) return;
    const category = classification(seed);
    if (!category) return;
    const excludedIds = new Set(
      cart.items.map((line) => String(line.product_id)),
    );
    const excludedHandles = new Set(cart.items.map(handleOf));
    const selected = [];
    const seen = new Set();
    async function collect(candidates) {
      const shortlist = (Array.isArray(candidates) ? candidates : [])
        .filter((candidate) => {
          const handle = handleOf(candidate);
          if (
            !handle ||
            seen.has(handle) ||
            excludedHandles.has(handle) ||
            excludedIds.has(String(candidate.id)) ||
            !physical(candidate)
          )
            return false;
          seen.add(handle);
          return true;
        })
        .slice(0, 10);
      const hydrated = await Promise.all(shortlist.map(hydrate));
      for (const product of hydrated) {
        if (selected.length >= limit) break;
        if (
          !product ||
          excludedIds.has(String(product.id)) ||
          !physical(product) ||
          product.requires_selling_plan ||
          classification(product) !== category ||
          !usableVariants(product).length
        )
          continue;
        selected.push(product);
      }
    }
    try {
      const recommended = await request(
        `recommendations/products.json?${new URLSearchParams({ product_id: String(seed.id), limit: "10", intent: "complementary" })}`,
      );
      await collect(recommended.products);
    } catch {
      /* Recommendations may be unavailable; use only category-matched fallback. */
    }
    if (selected.length < limit) {
      try {
        const query = new URLSearchParams({
          q: category,
          "resources[type]": "product",
          "resources[limit]": "10",
          "resources[options][unavailable_products]": "hide",
        });
        const search = await request(`search/suggest.json?${query}`);
        await collect(search.resources?.results?.products);
      } catch {
        /* No matching products is a valid empty recommendation result. */
      }
    }
    if (ownVersion !== recommendationVersion) return;
    const list = $("[data-cart-upsell-list]");
    list.replaceChildren(...selected.map(renderUpsell));
    area.hidden = selected.length === 0;
    state();
  }
  function renderUpsell(product) {
    const row = element("article", "outhavn-cart-upsell");
    row.dataset.productId = product.id;
    const variants = usableVariants(product);
    let variant =
      variants.find(
        (item) => String(item.id) === selections.get(product.handle),
      ) || variants[0];
    const picture = image(
      variant.featured_image || product.featured_image || product.images?.[0],
      product.title,
      "",
    );
    const body = element("div");
    const heading = element("h4");
    const link = element("a", "", product.title);
    link.href = productUrl(product);
    heading.append(link);
    const price = element(
      "p",
      "outhavn-cart-upsell-price",
      money(variant.price),
    );
    body.append(heading, price);
    const controls = element("div", "outhavn-cart-upsell-controls");
    if (product.variants.length > 1 || variant.title !== "Default Title") {
      const select = element("select");
      select.setAttribute("aria-label", `Choose variant for ${product.title}`);
      for (const entry of product.variants) {
        const option = element(
          "option",
          "",
          `${entry.title}${entry.available ? "" : " — Sold out"}`,
        );
        option.value = entry.id;
        option.disabled = !variants.includes(entry);
        option.selected = String(entry.id) === String(variant.id);
        select.append(option);
      }
      select.addEventListener("change", () => {
        variant =
          variants.find((entry) => String(entry.id) === select.value) ||
          variant;
        selections.set(product.handle, String(variant.id));
        price.textContent = money(variant.price);
        const nextImage = image(
          variant.featured_image ||
            product.featured_image ||
            product.images?.[0],
          product.title,
          "",
        );
        if (nextImage.src) picture.src = nextImage.src;
      });
      controls.append(select);
    }
    const add = button(
      "Add",
      `Add ${product.title} to cart`,
      "outhavn-cart-upsell-add",
    );
    add.dataset.upsellAdd = "";
    add.addEventListener("click", () => {
      if (checkoutBusy || pending || externalPending || !fresh) return;
      const variantId = variant.id;
      void enqueue(async () => {
        revision++;
        await request("cart/add.js", {
          method: "POST",
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        await readCart();
        announcement = "Item added to cart.";
      });
    });
    controls.append(add);
    body.append(controls);
    row.append(picture, body);
    return row;
  }
  function scheduleRefresh(shouldOpen) {
    if (shouldOpen && !dialog.open) {
      restoreFocus = document.activeElement;
      priorOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      dialog.showModal();
      $("[data-cart-close]").focus({ preventScroll: true });
    }
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      void refresh();
    }, 80);
    state();
  }
  function mutation(input, init) {
    try {
      const url = new URL(
        typeof input === "string" || input instanceof URL ? input : input.url,
        location.href,
      );
      const method = (init?.method || input?.method || "GET").toUpperCase();
      return url.origin === location.origin &&
        method !== "GET" &&
        method !== "HEAD"
        ? url.pathname.match(
            /\/cart\/(add|change|update|clear)(?:\.js)?\/?$/,
          )?.[1]
        : null;
    } catch {
      return null;
    }
  }
  function isCartRequest(input, init) {
    if (mutation(input, init)) return true;
    try {
      const url = new URL(
        typeof input === "string" || input instanceof URL ? input : input.url,
        location.href,
      );
      const method = (init?.method || input?.method || "GET").toUpperCase();
      return url.origin === location.origin && ["GET", "HEAD"].includes(method) &&
        /\/cart(?:\.(?:js|json)|\/(?:add|change|update|clear)(?:\.js)?)\/?$/.test(url.pathname);
    } catch {
      return false;
    }
  }
  function cartFetch(receiver, args) {
    if (!isCartRequest(args[0], args[1]))
      return Reflect.apply(baseFetch, receiver, args);
    // Shopify may establish a cart cookie on cart reads, including the theme's
    // GET /cart/update.js. A late
    // anonymous read must finish before an add starts, otherwise its Set-Cookie
    // can replace the just-created cart. The UI revision guard alone cannot
    // prevent that browser cookie race. Preserve the original response/body and
    // never replay a cart write, including when its outcome is unknown.
    const response = cartRequests.then(() => Reflect.apply(baseFetch, receiver, args));
    cartRequests = response.then(() => {}, () => {});
    return response;
  }
  function beginExternal() {
    externalPending++;
    revision++;
    fresh = false;
    state();
  }
  function endExternal(operation, succeeded) {
    externalPending--;
    if (!externalPending)
      for (const resolve of externalWaiters.splice(0)) resolve();
    // Re-read unsuccessful/unknown writes too, without changing their caller's result.
    scheduleRefresh(succeeded && operation === "add");
  }
  window.fetch = function (...args) {
    const operation = mutation(args[0], args[1]);
    const result = cartFetch(this, args);
    if (operation) {
      beginExternal();
      Promise.resolve(result)
        .then(
          (response) => endExternal(operation, response.ok),
          () => endExternal(operation, false),
        )
        .catch(() => {});
    }
    return result;
  };
  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  const xhrOperations = new WeakMap();
  XMLHttpRequest.prototype.open = function (...args) {
    const result = Reflect.apply(xhrOpen, this, args);
    xhrOperations.set(this, mutation(args[1], { method: args[0] }));
    return result;
  };
  XMLHttpRequest.prototype.send = function (...args) {
    const operation = xhrOperations.get(this);
    if (!operation) return Reflect.apply(xhrSend, this, args);
    beginExternal();
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      endExternal(operation, this.status >= 200 && this.status < 300);
    };
    this.addEventListener("loadend", finish, { once: true });
    try {
      return Reflect.apply(xhrSend, this, args);
    } catch (error) {
      this.removeEventListener("loadend", finish);
      finish();
      throw error;
    }
  };
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    close();
  });
  dialog.addEventListener("keydown", (event) => {
    if (event.key !== "Tab") return;
    const targets = [
      ...dialog.querySelectorAll(
        "a[href], button, input, select, textarea, [tabindex]",
      ),
    ].filter(
      (node) =>
        !node.disabled && node.tabIndex >= 0 && node.getClientRects().length,
    );
    const first = targets[0];
    const last = targets[targets.length - 1];
    if (
      (event.shiftKey && document.activeElement === first) ||
      (!event.shiftKey && document.activeElement === last)
    ) {
      event.preventDefault();
      (event.shiftKey ? last : first)?.focus();
    }
  });
  dialog.addEventListener("click", (event) => {
    const bounds = dialog.getBoundingClientRect();
    if (
      event.target === dialog &&
      (event.clientX < bounds.left ||
        event.clientX > bounds.right ||
        event.clientY < bounds.top ||
        event.clientY > bounds.bottom)
    )
      close();
  });
  $("[data-cart-close]").addEventListener("click", close);
  $("[data-cart-retry]").addEventListener("click", () => void refresh());
  $("[data-cart-checkout-form]").addEventListener("submit", (event) => {
    if (checkout.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  });
  window.addEventListener(
    "click",
    (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const trigger = event.target.closest?.(
        'modal-open[target="cart-drawer"], [data-outhavn-cart-open], a[href]',
      );
      if (!trigger || dialog.contains(trigger)) return;
      let opensCart = trigger.matches(
        'modal-open[target="cart-drawer"], [data-outhavn-cart-open]',
      );
      if (trigger.matches("a[href]")) {
        const url = new URL(trigger.href, location.href);
        opensCart =
          url.origin === location.origin &&
          url.pathname.replace(/\/$/, "") ===
            `${pathRoot}cart`.replace(/\/$/, "");
      }
      if (!opensCart) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      void open(trigger);
    },
    true,
  );
  window.addEventListener(
    "keydown",
    (event) => {
      if (event.repeat || (event.key !== "Enter" && event.key !== " ")) return;
      const trigger = event.target.closest?.(
        'modal-open[target="cart-drawer"], [data-outhavn-cart-open]',
      );
      if (!trigger || dialog.contains(trigger)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      void open(trigger);
    },
    true,
  );
  document.addEventListener("cart:refresh", () => scheduleRefresh(false));
  document.addEventListener("outhavn:checkout:state", (event) => {
    checkoutBusy = event.detail?.busy === true;
    if (checkoutBusy) clearError();
    state();
  });
  document.addEventListener("outhavn:checkout:error", (event) => {
    if (typeof event.detail?.message === "string") showError(event.detail.message);
  });
  // The installed theme's documented one-time mute removes only its old auto-open handler.
  customElements.whenDefined("cart-drawer").then(() => {
    for (const legacy of document.querySelectorAll("cart-drawer"))
      if (typeof legacy._handleMute === "function") legacy._handleMute();
  });
  document.body.append(dialog);
  window.OuthavnCart = {
    open,
    refresh,
    close,
    get pending() {
      return pending > 0 || externalPending > 0 || checkoutBusy;
    },
  };
  document.documentElement.classList.add("outhavn-native-cart-ready");
  state();
})();
